import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, PaginatedResponse } from "@/api/api";
import { OrganizationDataMinimal } from "@/organization/organization";
import { useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "@tanstack/react-router";
import { apiClient } from "@/api/client";

type OrganizationListOptions = {
  pageSize?: number;
  includeInactive?: boolean;
};

export const organizations = createQueryKeys('organizations', {
  listMinimal: (options: OrganizationListOptions = {}) => {
    const { pageSize = 100, includeInactive = false } = options;

    return {
      queryKey: ["listMinimal", options],
      queryFn: async () => {
        const params = new URLSearchParams({
          page_size: pageSize.toString(),
          minimal: 'true',
        });
        if (includeInactive) {
          params.set('include_inactive', 'true');
        }

        return await get<PaginatedResponse<OrganizationDataMinimal>>(
          `organizations/?${params.toString()}`
        );
      },
    };
  },
  detailMinimal: (organizationId: number) => ({
    queryKey: ["detailMinimal", organizationId],
    queryFn: async() => {
      return await get<OrganizationDataMinimal>(`organizations/${organizationId}/?minimal=True`);
    }
  })
});

export const useOrganizationsMinimal = (options?: OrganizationListOptions) => {
  return useSuspenseQuery(organizations.listMinimal(options));
};

export const useOrganizationMinimal = (organizationId: number) => {
  return useSuspenseQuery(organizations.detailMinimal(organizationId));
};

export const useSelectedOrganizationId = (): number => {
  const { organizationId } = useParams({ strict: false });
  if (!organizationId) {
    throw new Error("No organizationId in route params");
  }
  return organizationId;
};

export const useOrganizationIdWithFallback = (): number | null => {
  const { data: { results: allOrganizations } } = useOrganizationsMinimal();
  const { organizationId } = useParams({ strict: false });

  return useMemo(() => {
    if (organizationId) return organizationId;
    return allOrganizations[0]?.id ?? null;
  }, [organizationId, allOrganizations]);
};

export const useUpdateOrganizationActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { organizationId: number; is_active: boolean }) => {
      const res = await apiClient.patch(
        `organizations/${data.organizationId}/`,
        { is_active: data.is_active }
      );
      return res.data as OrganizationDataMinimal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizations._def });
    },
  });
};

